import React from 'react';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Field, OptionButton, keepVisible } from '@/components/booking/fields';
import { LuxuryCalendar } from '@/components/booking/LuxuryCalendar';
import { LAND_BANDS, LOT_LINE_NOTE, VIDEO_ADDON, GUARANTEE, LOCATION_LABELS, bandFor, landPrice } from '@/lib/vacantLand';
import { today, fmtDate } from '@/lib/bookFlow';
import { ShieldCheck } from 'lucide-react';

const textareaCls = 'min-h-[96px] text-base bg-[#0f1724] border-white/15 text-white placeholder:text-white/30 focus:border-[#C6A14A]';

export const Guarantee = () => (
  <div className="flex items-start gap-2 rounded-lg border border-emerald-500/25 bg-emerald-500/10 p-3 text-sm text-emerald-200" data-testid="land-guarantee">
    <ShieldCheck className="h-4 w-4 mt-0.5 shrink-0" />{GUARANTEE}
  </div>
);

export const PriceTable = ({ highlight }) => (
  <div className="rounded-xl border border-white/10 overflow-hidden text-sm" data-testid="land-price-table">
    <div className="grid grid-cols-3 bg-white/5 px-3 py-2 text-[#E8E4DE]/60 text-xs uppercase tracking-wider">
      <span>Land size</span><span className="text-right">Inside Kingman</span><span className="text-right">Outside</span>
    </div>
    {LAND_BANDS.map((b) => (
      <div key={b.key} className={`grid grid-cols-3 px-3 py-2 border-t border-white/5 ${highlight === b.key ? 'bg-[#C6A14A]/10 text-white' : 'text-[#E8E4DE]/80'}`}>
        <span>{b.label}</span><span className="text-right">${b.inside}</span><span className="text-right">${b.outside}</span>
      </div>
    ))}
    <div className={`grid grid-cols-3 px-3 py-2 border-t border-white/5 ${highlight === 'over_160' ? 'bg-[#C6A14A]/10 text-white' : 'text-[#E8E4DE]/80'}`}>
      <span>Over 160 acres</span><span className="col-span-2 text-right">Request a quote</span>
    </div>
  </div>
);

export const Row = ({ k, v }) => (
  <div className="flex justify-between gap-4 py-2.5 border-b border-white/5 last:border-0">
    <span className="text-[#E8E4DE]/50 text-sm">{k}</span><span className="text-white text-sm text-right">{v}</span>
  </div>
);

export const LandSummary = ({ form, contact }) => {
  const p = landPrice(form);
  return (
    <div className="rounded-xl border border-white/10 bg-[#0f1724] px-4" data-testid="land-summary">
      <Row k="Parcel" v={[form.parcelAddress, form.apn && `APN ${form.apn}`].filter(Boolean).join(' · ') || '—'} />
      <Row k="City / ZIP" v={[form.city, form.zipCode].filter(Boolean).join(' ') || '—'} />
      <Row k="Land size" v={form.acreage ? `${form.acreage} acres · ${p.band?.label || ''}` : '—'} />
      <Row k="Location" v={LOCATION_LABELS[form.location] || '—'} />
      <Row k="Preferred date" v={fmtDate(form.preferredDate)} />
      {form.accessNotes?.trim() && <Row k="Access notes" v={form.accessNotes} />}
      {contact?.contactName?.trim() && <Row k="Contact" v={[contact.contactName, contact.contactPhone].filter(Boolean).join(' · ')} />}
      {!p.quote && p.base > 0 && <Row k="Aerial photos" v={`$${p.base}`} />}
      {form.addVideo && !p.quote && <Row k="60-second aerial video" v={`$${VIDEO_ADDON}`} />}
      <div className="flex justify-between py-3">
        <span className="text-white font-medium">{p.quote ? 'Price' : 'Total due today'}</span>
        <span className="text-[#C6A14A] text-xl" data-testid="land-total">{p.quote ? 'Request a quote' : p.total ? `$${p.total}` : '—'}</span>
      </div>
    </div>
  );
};

const LOCATION_OPTIONS = [
  ['inside', 'Inside Kingman city limits', 'inside'],
  ['outside', 'Outside city limits', 'outside'],
  ['not_sure', 'Not sure', 'outside'],
];

// Each step: question, inputs and validation, shown one per screen.
export const buildLandSteps = ({ form, set, ref }) => {
  const band = bandFor(parseFloat(form.acreage));
  return [
    {
      key: 'parcel', title: 'Where is the land?', hint: 'Enter the parcel address, the APN, or both.',
      validate: () => (form.parcelAddress.trim() || form.apn.trim() ? null : 'Please enter the parcel address or APN'),
      body: (
        <>
          <Field ref={ref} id="land-address" label="Parcel address" autoComplete="street-address" placeholder="e.g. 1234 N Stockton Hill Rd" value={form.parcelAddress} onChange={(e) => set('parcelAddress', e.target.value)} />
          <Field id="land-apn" label="APN (parcel number)" placeholder="e.g. 306-12-034" value={form.apn} onChange={(e) => set('apn', e.target.value)} hint="Find it on your listing or the Mohave County parcel viewer." />
        </>
      ),
    },
    {
      key: 'location', title: 'City and ZIP',
      validate: () => (!form.city.trim() ? 'Please enter the city' : !/^\d{5}(-\d{4})?$/.test(form.zipCode.trim()) ? 'Please enter a 5-digit ZIP code' : null),
      body: (
        <div className="grid grid-cols-[1fr_8rem] gap-3">
          <Field ref={ref} id="land-city" label="City" required autoComplete="address-level2" placeholder="Kingman" value={form.city} onChange={(e) => set('city', e.target.value)} />
          <Field id="land-zip" label="ZIP" required inputMode="numeric" autoComplete="postal-code" placeholder="86401" maxLength={10} value={form.zipCode} onChange={(e) => set('zipCode', e.target.value)} />
        </div>
      ),
    },
    {
      key: 'acreage', title: 'How many acres?', hint: band ? `${band.label}${band.lotLines ? ' · includes lot-line overlay' : ''}` : 'Decimals are fine, e.g. 2.5',
      validate: () => (parseFloat(form.acreage) > 0 ? null : 'Please enter the land size in acres, e.g. 2.5'),
      body: (
        <>
          <Field ref={ref} id="land-acreage" label="Acreage" required type="text" inputMode="decimal" placeholder="2.5" value={form.acreage}
            onChange={(e) => set('acreage', e.target.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1'))} />
          {band?.lotLines && <p className="text-xs text-[#E8E4DE]/50">{LOT_LINE_NOTE}</p>}
          <PriceTable highlight={band?.key} />
        </>
      ),
    },
    {
      key: 'limits', title: 'Inside Kingman city limits?',
      hint: form.location === 'not_sure' ? 'We’ll charge the outside rate and refund the difference if it turns out to be inside city limits.' : null,
      validate: () => (form.location ? null : 'Please choose inside, outside or not sure'),
      body: (
        <div className="space-y-2" role="radiogroup" aria-label="City limits">
          {LOCATION_OPTIONS.map(([v, l, rate]) => (
            <OptionButton key={v} selected={form.location === v} onClick={() => set('location', v)} testId={`land-location-${v}`}>
              <span className="flex justify-between gap-3"><span>{l}</span>{band && !band.quote && <span className="text-[#C6A14A]">${band[rate]}</span>}</span>
            </OptionButton>
          ))}
        </div>
      ),
    },
    ...(band?.quote ? [] : [{
      key: 'video', title: 'Add a 60-second aerial video?', hint: `+$${VIDEO_ADDON} · great for listings and social posts.`,
      validate: () => null,
      body: (
        <div className="space-y-2" role="radiogroup" aria-label="Aerial video add-on">
          <OptionButton selected={form.addVideo} onClick={() => set('addVideo', true)} testId="land-video-yes">Yes, add video (+${VIDEO_ADDON})</OptionButton>
          <OptionButton selected={!form.addVideo} onClick={() => set('addVideo', false)} testId="land-video-no">No, photos only</OptionButton>
        </div>
      ),
    }]),
    {
      key: 'date', title: 'Preferred shoot date?', hint: "We'll confirm the exact time with you.",
      validate: () => (!form.preferredDate ? 'Please pick a preferred date' : form.preferredDate < today() ? 'Please pick today or a later date' : null),
      body: <LuxuryCalendar id="land-date" label="Preferred date" required min={today()} value={form.preferredDate} onChange={(v) => set('preferredDate', v)} testId="book-calendar" />,
    },
    {
      key: 'access', title: 'Anything we should know about access?', hint: 'Optional — gate codes, 4WD needed, where to park.',
      validate: () => null,
      body: (
        <div className="space-y-1.5">
          <Label htmlFor="land-access" className="text-[#E8E4DE]/80 text-sm">Access notes</Label>
          <Textarea ref={ref} id="land-access" onFocus={keepVisible} placeholder="Gate code 1234, dirt road — 4WD recommended" value={form.accessNotes} onChange={(e) => set('accessNotes', e.target.value)} className={textareaCls} data-testid="mobile-land-access" />
        </div>
      ),
    },
  ];
};
